// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import Button from "@guestsupply/MobiusOverrides/Button/Button";
import { ButtonPresentationProps } from "@guestsupply/MobiusOverrides/Button/Props";
import { xValidateOnSubmit } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import sendRmaRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SendRmaRequest";
import setReturnNotes from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetReturnNotes";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import Hidden from "@insite/mobius/Hidden";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showOnlyOnMobile = "showOnlyOnMobile",
    showOnlyOnLargeScreen = "showOnlyOnLargeScreen",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOnlyOnMobile]: boolean;
        [fields.showOnlyOnLargeScreen]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    orderDetailsLink: getPageLinkByPageType(state, "OrderDetailsPage"),
    resultMessage: state.pages.requestRma.resultMessage,
    canSendReturnRequest: state.pages.requestRma.canSendReturnRequest,
    properties: state.xRmaReturnInformation.properties,
    returnNotes: state.pages.requestRma.returnNotes,
    isSmallMobileScreen: state.xCommon.isSmallMobileScreen,
});

const mapDispatchToProps = {
    sendRmaRequest,
    setReturnNotes,
};

export interface RequestRmaCheckBoxConfirmationStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    buttonsWrapper?: InjectableCss;
    sendButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const requestRmaCheckBoxConfirmationStyles: RequestRmaCheckBoxConfirmationStyles = {};

const styles = requestRmaCheckBoxConfirmationStyles;

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const formId = "rmaRequestForm";

const RequestRmaCheckBoxConfirmation: React.FC<Props> = ({
    toaster,
    history,
    orderDetailsLink,
    resultMessage,
    properties,
    canSendReturnRequest,
    returnNotes,
    isSmallMobileScreen,
    fields: { showOnlyOnMobile, showOnlyOnLargeScreen },
    sendRmaRequest,
}) => {
    const { value: order } = useContext(OrderStateContext);

    const [isCheckboxChecked, setIsCheckboxChecked] = React.useState(false);
    const [isRequestSent, setIsRequestSent] = React.useState(false);

    if (!order) {
        return null;
    }

    const cancelClickHandle = () => {
        if (!orderDetailsLink) {
            return;
        }

        setReturnNotes({ returnNotes: "" });
        history.push(`${orderDetailsLink.url}?orderNumber=${order.erpOrderNumber?.trim() || order.webOrderNumber}`);
    };

    const sendReturnRequestHandler = () => {
        if (properties.UPSReturnLabel === "Email") {
            const errors = xValidateOnSubmit(formId);

            if (errors.length) {
                return;
            }
        }

        sendRmaRequest({
            orderNumber: order.erpOrderNumber?.trim() || order.webOrderNumber,
            onSuccess: () => {
                setIsRequestSent(true);
                toaster.addToast({
                    body: siteMessage(resultMessage || translate("Request submitted.")),
                    messageType: "success",
                });
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    if (showOnlyOnMobile && !isSmallMobileScreen) {
        return null;
    }

    return (
        <Hidden
            above={showOnlyOnMobile && isSmallMobileScreen ? "sm" : undefined}
            below={showOnlyOnLargeScreen && isSmallMobileScreen ? "sm" : undefined}
        >
            <StyledWrapper {...styles.wrapper} data-test-selector="rmaShippingInformation">
                <Typography {...styles.titleText}>
                    {siteMessage(
                        "CUSTOM_RMA_Terms_Checkbox",
                        "Please fill out the required information in the product table below to submit request.",
                    )}
                </Typography>
                <Checkbox checked={isCheckboxChecked || false} onChange={(_, checked) => setIsCheckboxChecked(checked)}>
                    {siteMessage(
                        "CUSTOM_RMA_Terms_Checkbox_Label",
                        "I accept any additional restocking fees which may apply",
                    )}
                </Checkbox>

                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        {...styles.cancelButton}
                        onClick={cancelClickHandle}
                        data-test-selector="requestRmaHeader_cancel"
                        aria-label={translate("Cancel")}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        onClick={sendReturnRequestHandler}
                        disabled={
                            !properties.UPSReturnLabel ||
                            (properties.UPSReturnLabel === "Email" && !properties.RMAEmail) ||
                            !canSendReturnRequest ||
                            !returnNotes ||
                            isRequestSent ||
                            !isCheckboxChecked
                        }
                        aria-label={translate("Request RMA")}
                        data-test-selector="requestRmaHeader_send"
                        {...styles.sendButton}
                    >
                        {translate("Request RMA")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Hidden>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(withToaster(RequestRmaCheckBoxConfirmation))),
    definition: {
        group: "Return Request (RMA)",
        allowedContexts: ["RequestRmaPage"],
        fieldDefinitions: [
            {
                name: fields.showOnlyOnMobile,
                displayName: "Show Only On Mobile",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
                isVisible: item => !item.fields.showOnlyOnLargeScreen,
            },
            {
                name: fields.showOnlyOnLargeScreen,
                displayName: "Show Only On Large Screen",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
                isVisible: item => !item.fields.showOnlyOnMobile,
            },
        ],
    },
};

export default widgetModule;
